/**
 * LDP de Packs — /market/:slug/bundles
 * Misma chrome que Home/PLP (header, footer, carrito).
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { Package } from "lucide-react";
import { loadMarketCatalog, type MarketCatalogResult } from "@/lib/market-catalog";
import { renderMarketPublicOffline } from "@/lib/market-public-offline-gate";
import { MarketStoreChrome } from "@/components/market/MarketStoreChrome";
import { BundleCoverMedia } from "@/components/bundles/BundleCoverMedia";
import { MarketBreadcrumb } from "@/components/market/MarketBreadcrumb";
import { MarketBundleSkeleton } from "@/components/market/MarketSkeletons";

const fetchCatalog = createServerFn({ method: "GET" })
  .inputValidator((slug: string) => slug)
  // @ts-expect-error ServerFnCtx framework type mismatch
  .handler(async ({ data: slug }): Promise<MarketCatalogResult> => {
    return loadMarketCatalog(slug, { request: getRequest() });
  });

const FONT_LINK = {
  rel: "stylesheet",
  href: "https://fonts.googleapis.com/css2?family=Hanken+Grotesk:wght@400;600;700;800&family=Inter:wght@400;500;600&display=swap",
};

export const Route = createFileRoute("/market_/$slug/bundles")({
  pendingComponent: MarketBundleSkeleton,
  pendingMs: 0,
  loader: async ({ params }) => fetchCatalog({ data: params.slug }),
  component: MarketBundlesLdp,
  head: ({ params, loaderData }) => {
    const result = loaderData as MarketCatalogResult | undefined;
    const name = result?.ok ? result.data.tenant.name : params.slug;
    return {
      meta: [
        { title: `Packs — ${name}` },
        { name: "description", content: `Armá tu pack en ${name}` },
      ],
      links: [{ rel: "manifest", href: `/api/manifest/${params.slug}` }, FONT_LINK],
    };
  },
});

function MarketBundlesLdp() {
  const { slug } = Route.useParams();
  const result = Route.useLoaderData() as MarketCatalogResult;

  const offline = renderMarketPublicOffline(result);
  if (offline) return offline;

  if (!result.ok) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6 text-center">
        <p className="text-[var(--market-on-surface-variant)]">Packs no disponibles.</p>
      </div>
    );
  }

  const data = result.data;
  const items = data.bundles ?? [];

  return (
    <MarketStoreChrome slug={slug} data={data} activeHref={`/market/${slug}/bundles`}>
      <div className="mx-auto max-w-6xl px-4 py-6 md:px-12">
        <MarketBreadcrumb
          items={[
            { label: data.tenant.name, href: `/market/${slug}` },
            { label: "Packs" },
          ]}
          className="mb-6"
        />

        <header className="mb-8">
          <h1 className="market-font-display text-3xl font-extrabold tracking-tight text-[var(--market-on-surface)]">Armá tu pack</h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[var(--market-on-surface-variant)]">
            Combiná lo que más te gusta y llevate un pack hecho a tu medida.
          </p>
        </header>

        {items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-[var(--market-surface-variant)] py-20 text-[var(--market-on-surface-variant)]">
            <Package className="h-10 w-10 opacity-40" />
            <p className="text-sm">Todavía no hay packs publicados.</p>
            <Link to="/market/$slug" params={{ slug }} className="text-sm font-medium text-[var(--market-accent)] underline-offset-2 hover:underline">
              Volver al inicio
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((b) => (
              <Link
                key={b.id}
                to="/market/$slug/bundle/$bundleSlug"
                params={{ slug, bundleSlug: b.slug }}
                className="group flex flex-col overflow-hidden rounded-2xl border border-[var(--market-surface-variant)] bg-[var(--market-surface-bright)] transition-shadow hover:shadow-md"
              >
                <BundleCoverMedia
                  imageUrl={b.imageUrl}
                  mosaic={b.imageMosaic}
                  className="aspect-[16/10]"
                  imgClassName="transition-transform duration-300 group-hover:scale-105"
                  alt={b.name}
                />
                <div className="flex flex-1 flex-col p-5">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--market-on-surface-variant)]">Pack</span>
                  <h2 className="mt-1 text-xl font-semibold tracking-tight text-[var(--market-on-surface)]">{b.name}</h2>
                  {b.description ? (
                    <p className="mt-2 line-clamp-3 text-sm text-[var(--market-on-surface-variant)]">{b.description}</p>
                  ) : null}
                  <span className="mt-4 text-sm font-semibold text-[var(--market-accent)]">Armar pack →</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </MarketStoreChrome>
  );
}
